import { useState, type FormEvent, type ReactNode } from "react";
import type { PhotoEvidence } from "../api/capture-types.js";
import type {
  CreatedSurveyShareLink,
  ShareLinkView,
  SurveyShareLink,
} from "../api/share-map-types.js";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { Async, Failure } from "./Async.jsx";
import { CopyField } from "./CopyField.jsx";
import { longDate, utcMoment } from "../lib/format.js";
import { HowToLink } from "../help/HowToLink.jsx";
import { href } from "../app/router.js";

/**
 * Share this survey — privately, read-only, for a while.
 *
 * The installer makes a link that opens this survey's floor plan and 3D model
 * (and, only if they tick them, chosen photographs) for somebody without a
 * Scannix login: the customer, or a subcontractor pricing the radiators. The
 * link lives in the customer app at `#/share/<token>`.
 *
 * What this panel is careful about:
 *
 *  - The link is shown ONCE, right after it is made. The server keeps only a
 *    hash of it, so there is nothing to show later — the panel says so, next
 *    to the Copy button, rather than letting someone discover it.
 *  - Photographs are OFF by default and picked one by one. They are the inside
 *    of somebody's home, printed with the GPS position they were taken at.
 *  - Every link ever made stays listed — live, expired or withdrawn — with how
 *    often it was opened, and the audit of each open is one click away.
 *  - Withdrawing is immediate and final.
 */

export const EXPIRY_CHOICES = [7, 14, 30, 60, 90] as const;
const DEFAULT_EXPIRY = 30;

/** The URL to hand out: the API's, else one composed from the portal's own setting. */
export function shareUrlFor(created: CreatedSurveyShareLink): string | null {
  if (created.url) return created.url;
  const base = (import.meta.env["VITE_CUSTOMER_APP_URL"] as string | undefined)?.replace(/\/+$/, "");
  return base ? `${base}/#/share/${encodeURIComponent(created.token)}` : null;
}

export function SurveySharePanel({
  surveyId,
  jobId,
  onClose,
}: {
  readonly surveyId: string;
  readonly jobId: string;
  readonly onClose: () => void;
}): ReactNode {
  const { client } = usePortal();
  const links = useAsync<SurveyShareLink[]>(
    async () => client.surveyShareLinks(surveyId),
    [client, surveyId],
  );
  const [created, setCreated] = useState<CreatedSurveyShareLink | null>(null);
  // A handover code for a job with no address is refused at the other end
  // (422 survey_not_exportable), so say so here — before the code is made and
  // read out — rather than after somebody has pasted it into portal.scannix.co.
  const readiness = useAsync<boolean>(
    async () => (await client.designReadiness(surveyId)).jobHasAddress,
    [client, surveyId],
  );
  const noAddress = readiness.state.status === "ready" && readiness.state.data === false;

  return (
    <section className="survey-share" aria-labelledby="survey-share-heading">
      <div className="spread">
        <h2 id="survey-share-heading">Share this survey</h2>
        <button type="button" className="secondary" onClick={onClose}>
          Close
        </button>
      </div>
      <p className="muted small">
        A private, read-only link to this survey&rsquo;s floor plan and 3D model
        for someone without a Scannix login — the customer, or a subcontractor.
        It expires, you can withdraw it at any time, and every time it is opened
        is recorded. It is not listed or searchable anywhere.{" "}
        <HowToLink guide="sharing-a-survey">How sharing works</HowToLink>
      </p>
      {noAddress ? (
        <div className="notice warn" role="status">
          <p className="small">
            This job has no address or postcode yet, so portal.scannix.co will refuse its handover code.{" "}
            <a href={href.job(jobId)}>Add the customer and address on the job</a> first — the link itself works
            either way.
          </p>
        </div>
      ) : null}

      {created ? (
        <NewLink created={created} onDone={() => setCreated(null)} />
      ) : (
        <CreateForm
          surveyId={surveyId}
          jobId={jobId}
          onCreated={(result) => {
            setCreated(result);
            links.reload();
          }}
        />
      )}

      <h3>Links made for this survey</h3>
      <Async state={links.state} what="the share links" reload={links.reload}>
        {(list) => <LinkList surveyId={surveyId} links={list} onChanged={links.reload} />}
      </Async>
    </section>
  );
}

function CreateForm({
  surveyId,
  jobId,
  onCreated,
}: {
  readonly surveyId: string;
  readonly jobId: string;
  readonly onCreated: (created: CreatedSurveyShareLink) => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const [label, setLabel] = useState("");
  const [days, setDays] = useState<number>(DEFAULT_EXPIRY);
  const [includePhotos, setIncludePhotos] = useState(false);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    void submit(async () => {
      const result = await client.createSurveyShareLink(surveyId, {
        expiresInDays: days,
        ...(label.trim() ? { label: label.trim() } : {}),
        photoIds: includePhotos ? [...picked] : [],
      });
      setLabel("");
      setIncludePhotos(false);
      setPicked(new Set());
      onCreated(result);
    });
  }

  return (
    <form className="survey-share__form" onSubmit={onSubmit} aria-label="New share link">
      <div className="survey-share__row">
        <label className="field">
          <span>Who is it for? (only you see this)</span>
          <input
            type="text"
            value={label}
            maxLength={120}
            placeholder="e.g. Bob's Plumbing — radiator quote"
            onChange={(e) => setLabel(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Link stops working after</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {EXPIRY_CHOICES.map((d) => (
              <option key={d} value={d}>
                {d} days
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="survey-share__check">
        <input
          type="checkbox"
          checked={includePhotos}
          onChange={(e) => setIncludePhotos(e.target.checked)}
        />
        Include photographs I choose
      </label>
      {includePhotos ? (
        <PhotoPicker jobId={jobId} picked={picked} onChange={setPicked} />
      ) : null}

      {error ? <Failure error={error} what="the share link" /> : null}
      <div className="row-actions">
        <button type="submit" className="primary" disabled={busy}>
          {busy ? "Making the link…" : "Make a share link"}
        </button>
      </div>
    </form>
  );
}

function PhotoPicker({
  jobId,
  picked,
  onChange,
}: {
  readonly jobId: string;
  readonly picked: ReadonlySet<string>;
  readonly onChange: (next: ReadonlySet<string>) => void;
}): ReactNode {
  const { client } = usePortal();
  const photos = useAsync<readonly PhotoEvidence[]>(
    async () => client.jobPhotos(jobId),
    [client, jobId],
  );

  const toggle = (id: string) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  return (
    <fieldset className="survey-share__photos">
      <legend>Photographs to include</legend>
      <p className="notice small" role="note">
        Photographs show the inside of the home and are printed with the GPS
        position they were taken at. Include only what the recipient needs.
      </p>
      <Async state={photos.state} what="the photographs" reload={photos.reload}>
        {(list) =>
          list.length === 0 ? (
            <p className="empty">No photographs have been taken on this job.</p>
          ) : (
            <>
              {/* A handover to another portal wants the whole record, and
                  ticking twenty boxes to get it is how photographs get left
                  behind. Still opt-in, per photograph, for every other use. */}
              <div className="row-actions">
                <button
                  type="button"
                  className="secondary"
                  onClick={() => onChange(new Set(list.map((p) => p.id)))}
                >
                  Select all {list.length}
                </button>
                <button type="button" className="secondary" onClick={() => onChange(new Set())}>
                  Select none
                </button>
              </div>
              <ul className="survey-share__photo-list">
              {list.map((photo) => (
                <li key={photo.id}>
                  <label>
                    <input
                      type="checkbox"
                      checked={picked.has(photo.id)}
                      onChange={() => toggle(photo.id)}
                    />
                    {photo.description ?? photo.capturedDescription ?? photo.subject}
                    <span className="muted small"> · {utcMoment(photo.capturedAt)}</span>
                  </label>
                </li>
              ))}
              </ul>
            </>
          )
        }
      </Async>
    </fieldset>
  );
}

function NewLink({
  created,
  onDone,
}: {
  readonly created: CreatedSurveyShareLink;
  readonly onDone: () => void;
}): ReactNode {
  const url = shareUrlFor(created);
  const text = url ?? `#/share/${created.token}`;

  return (
    <div className="survey-share__created notice" role="status">
      <p>
        <strong>Link made.</strong> It works until{" "}
        {longDate(created.link.expiresAt)}
        {created.link.photoCount > 0
          ? ` and includes ${created.link.photoCount} photograph${created.link.photoCount === 1 ? "" : "s"}`
          : " and includes no photographs"}
        .
      </p>
      <CopyField label="Share link" value={text} copies="share link" hint="Opens the survey read-only in a browser." />
      {/* The SAME credential, given plainly, because the other portal asks for a
          code and not a URL: pasting a whole link into a code box is a mistake
          worth designing out rather than explaining afterwards. */}
      <CopyField
        label="Handover code for portal.scannix.co"
        value={created.token}
        copies="handover code"
        hint="Paste this into portal.scannix.co → Get a survey onto this job → Import from Scannix. It pulls this survey, its scan and its photographs in one go."
      />
      <div className="row-actions">
        <button type="button" className="secondary" onClick={onDone}>
          Done
        </button>
      </div>
      <p className="small">
        Copy it now: for security only a fingerprint of the link is kept, so it
        cannot be shown again. If it is lost, withdraw it and make another.
      </p>
      {url === null ? (
        <p className="small">
          This server does not know the customer app&rsquo;s address
          (CUSTOMER_APP_URL), so this is the end of the link only — put the
          customer app&rsquo;s address in front of it.
        </p>
      ) : null}
    </div>
  );
}

const STATUS_PILL: Record<SurveyShareLink["status"], { className: string; text: string }> = {
  live: { className: "pill pill--ok", text: "Live" },
  expired: { className: "pill", text: "Expired" },
  revoked: { className: "pill pill--danger", text: "Withdrawn" },
};

function LinkList({
  surveyId,
  links,
  onChanged,
}: {
  readonly surveyId: string;
  readonly links: readonly SurveyShareLink[];
  readonly onChanged: () => void;
}): ReactNode {
  if (links.length === 0) {
    return <p className="empty">No links have been made for this survey.</p>;
  }
  return (
    <div className="table-scroll">
      <table className="survey-share__table">
        <thead>
          <tr>
            <th scope="col">For</th>
            <th scope="col">Status</th>
            <th scope="col">Expires</th>
            <th scope="col">Photos</th>
            <th scope="col">Opened</th>
            <th scope="col">
              <span className="visually-hidden">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {links.map((link) => (
            <LinkRow key={link.id} surveyId={surveyId} link={link} onChanged={onChanged} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LinkRow({
  surveyId,
  link,
  onChanged,
}: {
  readonly surveyId: string;
  readonly link: SurveyShareLink;
  readonly onChanged: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const [confirming, setConfirming] = useState(false);
  const [showViews, setShowViews] = useState(false);
  const pill = STATUS_PILL[link.status];
  const who = link.label ?? "Unlabelled link";

  return (
    <>
      <tr>
        <td>
          {who}
          <div className="muted small">
            Made {longDate(link.createdAt)}
            {link.createdByName ? ` by ${link.createdByName}` : ""}
          </div>
        </td>
        <td>
          <span className={pill.className}>{pill.text}</span>
        </td>
        <td>{link.status === "revoked" && link.revokedAt ? `Withdrawn ${longDate(link.revokedAt)}` : longDate(link.expiresAt)}</td>
        <td>{link.photoCount === 0 ? "None" : link.photoCount}</td>
        <td>
          {link.viewCount === 0 ? "Not yet" : `${link.viewCount} time${link.viewCount === 1 ? "" : "s"}`}
          {link.lastViewedAt ? (
            <div className="muted small">Last {utcMoment(link.lastViewedAt)}</div>
          ) : null}
        </td>
        <td className="row-actions">
          <button
            type="button"
            className="link"
            aria-expanded={showViews}
            onClick={() => setShowViews((v) => !v)}
          >
            {showViews ? "Hide opens" : "Opens"}
          </button>
          {link.status === "live" ? (
            confirming ? (
              <>
                <button
                  type="button"
                  className="danger"
                  disabled={busy}
                  onClick={() =>
                    void submit(async () => {
                      await client.revokeSurveyShareLink(surveyId, link.id);
                      setConfirming(false);
                      onChanged();
                    })
                  }
                >
                  {busy ? "Withdrawing…" : `Withdraw "${who}" now`}
                </button>
                <button type="button" className="secondary" onClick={() => setConfirming(false)}>
                  Keep
                </button>
              </>
            ) : (
              <button type="button" className="secondary" onClick={() => setConfirming(true)}>
                Withdraw
              </button>
            )
          ) : null}
        </td>
      </tr>
      {error ? (
        <tr>
          <td colSpan={6}>
            <Failure error={error} what="the withdrawal" />
          </td>
        </tr>
      ) : null}
      {showViews ? (
        <tr>
          <td colSpan={6}>
            <Views surveyId={surveyId} linkId={link.id} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

const RESOURCE_WORDS: Record<ShareLinkView["resource"], string> = {
  survey: "Opened the page",
  scan: "Loaded the 3D model",
  photo: "Viewed a photograph",
};

function Views({ surveyId, linkId }: { readonly surveyId: string; readonly linkId: string }): ReactNode {
  const { client } = usePortal();
  const views = useAsync<ShareLinkView[]>(
    async () => client.surveyShareLinkViews(surveyId, linkId),
    [client, surveyId, linkId],
  );
  return (
    <Async state={views.state} what="the record of opens" reload={views.reload}>
      {(list) =>
        list.length === 0 ? (
          <p className="empty">Nobody has opened this link.</p>
        ) : (
          <ul className="survey-share__views" aria-label="Opens of this link">
            {list.map((v, i) => (
              <li key={`${v.viewedAt}-${i}`}>
                <span className="mono small">{utcMoment(v.viewedAt)}</span> — {RESOURCE_WORDS[v.resource]}
                {v.ipAddress ? <span className="muted small"> · from {v.ipAddress}</span> : null}
                {v.userAgent ? <span className="muted small"> · {v.userAgent.slice(0, 80)}</span> : null}
              </li>
            ))}
          </ul>
        )
      }
    </Async>
  );
}
